import React from 'react'
import { Box, Button, SxProps } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { Day, Days } from '@e3dc-react/shell/Libs/date/types'
import { useWeekday } from '@e3dc-react/shell/Libs/date'

interface StyleProps {
  buttonColor?: string
}

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  root: {
    display: 'flex',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: theme.shape?.dayButton.size,
    minHeight: theme.shape?.dayButton.size,
    width: theme.shape?.dayButton.size,
    minWidth: theme.shape?.dayButton.size,
    borderRadius: '50%',
    margin: theme.spacing(0.5),
    [theme.breakpoints.down('md')]: {
      height: '36px',
      minHeight: '36px',
      width: '36px',
      minWidth: '36px',
      borderRadius: '50%',
      marginTop: 0,
    },
    [theme.breakpoints.down('sm')]: {
      height: '33px',
      minHeight: '33px',
      width: '33px',
      minWidth: '33px',
    },
    [theme.breakpoints.between(0, 300)]: {
      height: '30px',
      minHeight: '30px',
      width: '30px',
      minWidth: '30px',
      borderRadius: '50%',
      marginTop: 0,
    },
    [theme.breakpoints.between('xs', 'xl')]: {
      marginBottom: theme.spacing(1),
    },
  },
  inactive: {
    border: 'none',
    backgroundColor: theme.palette.background.weekDayButtonInactive,
    color: theme.palette.grey.grey01,
    transition: 'all 0.1s ease-in-out',
    '@media (hover: hover)': {
      '&:hover': {
        transition: 'all 0.1s ease-in-out',
        outline: 'none',
        backgroundColor: props.buttonColor ? props.buttonColor : theme.palette.primary.light,
        border: `2px solid ${theme.palette.primary.dark}`,
      },
    },
    '&.MuiButton-root.Mui-disabled': {
      opacity: 0.7,
    },
  },
  active: {
    backgroundColor: props.buttonColor ? props.buttonColor : theme.palette.background.weekDayButtonActive,
    transition: 'all 0.1s ease-in-out',
    color: '#ffffff',
    '@media (hover: hover)': {
      '&:hover': {
        outline: 'none',
        transition: 'all 0.1s ease-in-out',
        backgroundColor: theme.palette.primary.dark,
        color: '#ffffff',
        border: `2px solid ${theme.palette.primary.main}`,
      },
    },
    '&.MuiButton-root.Mui-disabled': {
      opacity: 0.7,
    },
  },
  highlightedText: {
    color: theme.palette.primary.main,
    paddingLeft: theme.spacing(1),
  },
}))

export interface WeekDaySelectorProps {
  days?: Days
  onSelectDay: (dayName: Day, selected: boolean) => void
  className?: string
  disabledDays?: Day[]
  buttonColor?: string
  disabled?: boolean
  highlighted?: boolean
  sx?: SxProps
}

const WeekDaySelector = ({
  days,
  onSelectDay,
  className,
  disabledDays,
  buttonColor,
  disabled,
  highlighted = false,
  sx,
}: WeekDaySelectorProps): React.ReactElement => {
  const { classes, cx } = useStyles({ buttonColor })
  const { translatedWeekdaysShort } = useWeekday()
  const selectedDays: string[] = days
    ? Object.entries(days).reduce((prev: string[], current) => {
        if (current[1]) return [...prev, current[0]]
        return prev
      }, [])
    : []

  const onDayClick = (dayName: Day): void => {
    const wasSelected = selectedDays.includes(dayName.slice(0, 2))
    onSelectDay(dayName, !wasSelected)
  }

  return (
    <Box className={cx(classes.root, className)} sx={sx} data-qa="week-day-selector">
      {Object.entries(translatedWeekdaysShort).map(([dayName, shortName]) => (
        <Button
          disabled={(disabledDays?.includes(dayName.slice(0, 2) as Day) && !selectedDays?.includes(dayName.slice(0, 2))) || disabled}
          className={cx(classes.button, selectedDays?.includes(dayName.slice(0, 2)) ? classes.active : classes.inactive)}
          key={dayName}
          onClick={() => onDayClick(dayName.slice(0, 2) as Day)}
          data-qa={`button-week-day-selector-${shortName}`}
        >
          {shortName as Day}
        </Button>
      ))}
      {highlighted && <span className={classes.highlightedText}>✱</span>}
    </Box>
  )
}

export default WeekDaySelector
